import React, { memo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import ContractsRowActions from './ContractsRowActions';
import { usePermissions } from '../../hooks/usePermissions';
import ColumnMenu from './components/ColumnMenu.js';

import { formatDateForDisplay, parseSheetDate } from './utils/dates';
import { isInvygoContract, contractCategory } from './contracts.logic';
import { getIssueForRow } from './utils/analysis';
import { mustSwitchBack } from './utils/switchBack';
import { normalize as normalizeStr } from './utils/normalize';
import { hasNoRent, dailyEjarBalance, noBalanceNote } from './utils/liveBalance';
import { useEjarBalances } from '../../services/ejarBalances';
import { dubaiToday } from '../../services/contractRules';
import { phoneDigits, formatPhone } from '../../utils/phoneUtils';
import { formatContractDateTime } from '../../utils/contractDateUtils';

import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import useViewportFit from '../../ui/Table/useViewportFit';
import { CellText, CellId, CellPlate, CellPhone, CellTag, CellMuted, toneClass } from '../../ui/DataTable/cells';
import EmptyState from '../../ui/EmptyState';
import Badge from '../../ui/Badge';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';

const parseNumber = (val) => {
    if (typeof val === 'number') return val;
    if (!val) return 0;
    return parseFloat(val.toString().replace(/,/g, '')) || 0;
};

/**
 * Pick-up / Drop-off Date cell: "dd/mm/yyyy HH:MM" when the value has a time, the date alone when it
 * has none (owner, 27 Sep 2026). The old sheet's "dd-mm-yyyyHHMM" is read as a date and a time too;
 * anything the shared helper cannot read is shown the old way (date only).
 */
const LEGACY_DATE_TIME = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(\d{2})(\d{2})$/;
export const contractDateCell = (val) => {
    const legacy = typeof val === 'string' ? LEGACY_DATE_TIME.exec(val.trim()) : null;
    const text = formatContractDateTime(legacy ? `${legacy[1]}/${legacy[2]}/${legacy[3]} ${legacy[4]}:${legacy[5]}` : val);
    return text || (typeof val === 'string' ? formatDateForDisplay(val) : '');
};

// Internal optimized component for editing
const CopyMenu = ({ row, coords, onClose, onCopy, switchBackOf }) => {
    const getShortName = (name) => {
        if (!name) return "";
        const parts = name.trim().split(/\s+/);
        return parts.slice(0, 2).join(' ');
    };

    const customer = getShortName(row.customer || "");
    const phone = formatPhone(phoneDigits(row.phoneNumber));   // "+966540550044": the country code stays (27 Sep 2026)
    const booking = row.contractNo || row.bookingNumber || row['Booking Number'] || row['Booking ID'] || 'monthly';
    
    const options = [
        { label: 'Switch', value: 'Switch', icon: ICONS.refresh },
        { label: 'Service', value: 'Service', icon: ICONS.maintenance },
        { label: 'Battery', value: 'Battery Issue', icon: ICONS.alert },
        { label: 'Oil Change', value: 'Oil Change', icon: ICONS.settings }
    ];

    const handleSelect = (choice) => {
        let text;
        const needsSwitchBack = !!(switchBackOf && switchBackOf(row));
        
        const header = `${booking} - ${needsSwitchBack && choice === 'Switch' ? 'Switch Back' : choice}`;
        const customerInfo = `${customer} - ${phone}`;
        const oldCarInfo = `Old Car / ${row.ejarModel} - ${row.ejarPlate}`;
        
        if (needsSwitchBack && choice === 'Switch') {
            text = `${header}\n\n${customerInfo}\n\n${oldCarInfo}\n\nNew car / ${row.invygoModel} - ${row.invygoPlate}`;
        } else {
            const includeNewCar = (choice === 'Switch' || choice === 'Service');
            text = `${header}\n\n${customerInfo}\n\n${oldCarInfo}${includeNewCar ? '\n\nNew car /' : ''}`;
        }
        
        onCopy(text, "WhatsApp message copied!");
        onClose();
    };

    const menu = (
        <div className="fixed inset-0 z-[60] flex items-start justify-start" style={{ pointerEvents: 'none' }}>
           <div
               className="fixed inset-0 bg-transparent"
               onClick={onClose}
               style={{ pointerEvents: 'auto' }}
           />
           <div
               role="menu"
               aria-label="Copy WhatsApp text"
               className="absolute flex flex-col overflow-hidden w-44 bg-white border border-gray-200 rounded-lg shadow-md"
               style={{
                   top: coords.top,
                   left: coords.left,
                   transform: coords.isUp ? 'translateY(-100%)' : 'none',
                   pointerEvents: 'auto',
               }}
           >
               <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-[11px] uppercase font-semibold text-gray-500 tracking-wide">
                   Select Action
               </div>

               <div className="py-1">
                   {options.map((opt) => (
                       <button
                           key={opt.value}
                           type="button"
                           role="menuitem"
                           onClick={() => handleSelect(opt.value)}
                           className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                       >
                           <Icon glyph={opt.icon} size="sm" tone="muted" />
                           <span>{opt.label}</span>
                       </button>
                   ))}
               </div>
           </div>
        </div>
    );

    return ReactDOM.createPortal(menu, document.body);
};

const EditableCell = ({ initialValue, onSave, label }) => {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const handleBlur = () => {
        if (value !== initialValue) {
            onSave(value);
        }
    };

    return (
        <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={handleBlur}
            aria-label={label}
            className="w-full px-1.5 py-1 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
        />
    );
};

const ContractsTable = ({
    data, headers, visibleColumns, columnWidths,
    onColumnResize, onToggleColumnMenu, showColumnMenu,
    setShowColumnMenu, setVisibleColumns, headerDisplayNames, isAdmin,
    duplicatedContractsInfo, maintenanceData,
    // Switch Back (utils/switchBack via contracts.logic): `switchBackOf(row)` -> {via, since, days} | null; and
    // `onSkipSwitchBack(row)` - the customer keeps the car they have (never Invygo; none = no Skip button)
    // `inMismatchOf(row)`: the Mismatch tab's test (switched, its own car not Rented elsewhere / gone)
    switchBackOf, inMismatchOf, onSkipSwitchBack,
    isCarExpired, getExpiryDate,
    onCustomerClick, onCopy,
    isEditing, editedData, setEditedData, filterMode,
    // Edit Mode: only these columns become inputs (the contracts store: the staff-owned ones); none given = every one
    editableColumns,
    // a filter tab whose rows the worker has not counted yet: an empty table says 'Counting…', not "No contracts found"
    pending = false,
    paddingTop: _paddingTop, paddingBottom: _paddingBottom, visibleData: _visibleData, start: _start,
    className, fillViewport = false
}) => {
    // opening the calculator and the WhatsApp menu: each its own permission (permissions phase 3, 4 Oct 2026)
    const { hasPermission } = usePermissions();
    const canOpenCalculator = hasPermission('open_monthly_calculator');
    const canWhatsApp = hasPermission('contracts_send_whatsapp');
    const [copyMenu, setCopyMenu] = useState(null); // { row, coords }
    // Daily contracts show Ejar's own balance (the engine's saved read - no Ejar call per row).
    const ejarBalances = useEjarBalances();
    // fillViewport: the table takes all the height left below it on screen (rows scroll inside,
    // header stays sticky, the page itself does not scroll).
    const fit = useViewportFit(fillViewport, { min: 420 });
    const fillHeight = fit.height;
    // RENDER FULL TABLE AT ONCE (No Virtualization)
    // Warning: This may have performance impact on large datasets
    const rowsToRender = data;
    const effectiveStart = _start || 0;
    const topSpacer = 0;
    const bottomSpacer = 0;

    // Dynamic table based on visibleColumns and headers
    const finalHeaders = (visibleColumns ? headers.filter(h => visibleColumns.includes(h)) : headers);

    return (
        <div
            ref={fit.ref}
            className={`flex flex-col ${className || (fillViewport ? '' : 'h-[calc(100vh-180px)]')}`}
            style={fillViewport && fillHeight ? { height: fillHeight } : undefined}
        >
            <div
                className="table-card relative flex-1 min-h-0 overflow-auto"
            >
                <Table sticky style={{ tableLayout: 'auto' }}>
                    <TableHead>
                        <TableRow>
                            <TableHeader className="w-12">
                                #
                            </TableHeader>

                            {finalHeaders.map((headerKey) => (
                                <TableHeader key={headerKey} className="whitespace-nowrap">
                                    <div className="flex items-center justify-center gap-2 min-h-[28px]">
                                        <span>{headerDisplayNames[headerKey] || headerKey}</span>
                                        {/* Column-visibility menu trigger, inline after the title. The transform on this wrapper anchors the fixed-position menu to it (as before). */}
                                        <div className="shrink-0 translate-x-0">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onToggleColumnMenu(headerKey);
                                                }}
                                                className="p-1 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-200"
                                                aria-label={`Columns: show or hide (${headerDisplayNames[headerKey] || headerKey})`}
                                                title="Show / hide columns"
                                                aria-haspopup="true"
                                                aria-expanded={showColumnMenu === headerKey}
                                            >
                                                <Icon glyph={ICONS.more} size="xs" />
                                            </button>
                                            {showColumnMenu === headerKey && (
                                                <ColumnMenu
                                                    headers={headers}
                                                    visibleColumns={visibleColumns}
                                                    setVisibleColumns={setVisibleColumns}
                                                    setShowColumnMenu={setShowColumnMenu}
                                                    headerDisplayNames={headerDisplayNames}
                                                    isAdmin={isAdmin}
                                                />
                                            )}
                                        </div>
                                        <div
                                            className="absolute -right-3 top-0 bottom-0 w-1 cursor-col-resize hover:bg-gray-300"
                                            aria-hidden="true"
                                            onMouseDown={(e) => {
                                                const startX = e.pageX;
                                                const startWidth = columnWidths[headerKey] || 150;
                                                const handleMouseMove = (moveEvent) => {
                                                    const newWidth = Math.max(50, startWidth + (moveEvent.pageX - startX));
                                                    onColumnResize(headerKey, newWidth);
                                                };
                                                const handleMouseUp = () => {
                                                    document.removeEventListener('mousemove', handleMouseMove);
                                                    document.removeEventListener('mouseup', handleMouseUp);
                                                };
                                                document.addEventListener('mousemove', handleMouseMove);
                                                document.addEventListener('mouseup', handleMouseUp);
                                            }}
                                        />
                                    </div>
                                </TableHeader>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {/* Top Spacer - REQUIRED for Virtualization (Invisible) */}
                        {topSpacer > 0 && <tr style={{ height: topSpacer, border: 'none' }} />}

                        {rowsToRender.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={finalHeaders.length + 1}>
                                    {pending
                                        ? <EmptyState icon={ICONS.contracts} title="Counting…" description="This filter's contracts are being counted." />
                                        : <EmptyState icon={ICONS.contracts} title="No contracts found" description="Try another filter or search term." />}
                                </TableCell>
                            </TableRow>
                        )}

                        {rowsToRender.map((row, idx) => {
                            // Use calculated index for correct numbering
                            const i = effectiveStart + idx;

                            // Optimized: Skip heavy calculations if showing ALL contracts (Speed up Main View)
                            // User requested: "Don't restrict main table with calculations"
                            const isMainView = !filterMode || filterMode === 'all' || filterMode === 'open';

                            const plate = normalizeStr(row.invygoPlate);
                            // Only check duplicates if user wants, or skip for speed.
                            const isDuplicated = !isMainView && duplicatedContractsInfo[plate]?.length > 1;

                            // Switch / Switch Back: the tabs' own rule (utils/switchBack - owner, 4 Nov 2026)
                            const mismatch = row.type === 'open' && !!inMismatchOf && inMismatchOf(row);
                            const switchBack = mismatch && switchBackOf ? switchBackOf(row) : null;

                            const plates = [row.ejarPlate, row.invygoPlate, row.plateNo1, row.plateNo2].filter(Boolean);
                            const hasExpiredCar = row.type === 'open' && plates.some(p => isCarExpired(p));
                            // const expiryDate = hasExpiredCar ? getExpiryDate(plate) : null;

                            const copyIndexText = (e) => {
                                if (row.type !== 'open') return;
                                const rect = e.currentTarget.getBoundingClientRect();
                                const spaceBelow = window.innerHeight - rect.bottom;
                                const isUp = spaceBelow < 200;

                                setCopyMenu({
                                    row,
                                    coords: {
                                        top: isUp ? rect.top - 5 : rect.bottom + 5,
                                        left: rect.left,
                                        isUp
                                    }
                                });
                            };

                            const isSwitchBack = !!switchBack;

                            // --- Booking Expiry Logic (same as Airport Dashboard) ---
                            const dropoff = parseSheetDate(row.dropoffDate);
                            const today = dubaiToday();   // the Dubai calendar day, as the tab badge (CON-24)

                            let isBookingExpired = false;
                            let isBookingToday = false;

                            if (row.type === 'open' && dropoff) {
                                const dropoffTime = dropoff.getTime();
                                if (dropoffTime < today.getTime()) {
                                    isBookingExpired = true;
                                } else if (dropoffTime === today.getTime()) {
                                    isBookingToday = true;
                                }
                            }


                            // Row status (same precedence as before), shown as a thin left stripe on the first cell.
                            let rowStatus = null;
                            if (isSwitchBack) {
                                rowStatus = { tone: 'success', label: 'Switch back: own car ready' };
                            } else if (isBookingExpired) {
                                rowStatus = { tone: 'danger', label: 'Booking expired' };
                            } else if (isBookingToday) {
                                rowStatus = { tone: 'warning', label: 'Booking ends today' };
                            } else if (hasExpiredCar) {
                                rowStatus = { tone: 'warning', label: 'Car registration expired' };
                            } else if (mismatch) {
                                rowStatus = { tone: 'info', label: 'Switched: on another car than its own' };
                            }
                            const stripeStyle = rowStatus ? { boxShadow: `inset 3px 0 0 var(--${rowStatus.tone})` } : undefined;

                            return (
                                <TableRow key={row.id || i} title={rowStatus ? rowStatus.label : undefined}>
                                    <TableCell style={stripeStyle} className="whitespace-nowrap">
                                        <button
                                            type="button"
                                            onClick={copyIndexText}
                                            className="tabular-nums text-gray-600 hover:text-purple-700 hover:underline"
                                            title="Copy WhatsApp text"
                                        >
                                            {(row.originalIndex !== undefined ? row.originalIndex : i) + 1}
                                        </button>
                                        {rowStatus && <span className="sr-only">{rowStatus.label}</span>}
                                    </TableCell>

                                    {finalHeaders.map(headerKey => {
                                        let content = row[headerKey];
                                        const val = row[headerKey];

                                        if (['pickupDate', 'dropoffDate', 'replacementDate', 'revenueDate'].includes(headerKey)) {
                                            // Drop-off date: red when the booking has expired, amber when it ends today.
                                            const dateTone = headerKey === 'dropoffDate'
                                                ? (isBookingExpired ? 'danger' : isBookingToday ? 'warning' : undefined)
                                                : undefined;
                                            // Pick-up / Drop-off: with the time (owner, 27 Sep 2026); the other two stay dates.
                                            const shown = (headerKey === 'pickupDate' || headerKey === 'dropoffDate')
                                                ? contractDateCell(val) : formatDateForDisplay(val);
                                            content = <span className={`ds-cell-date ${toneClass(dateTone)}`}>{shown}</span>;
                                        } else if (headerKey === 'contractNo') {
                                            content = (
                                                <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                                                    <a
                                                        href="https://ejarprod.iyelo.com:6300//app/rental/contracts"
                                                        onClick={(e) => { e.preventDefault(); onCopy(val, `Contract ${val} copied!`); window.open(e.currentTarget.href, "_blank"); }}
                                                        className="hover:underline"
                                                    >
                                                        <CellId>{val}</CellId>
                                                    </a>
                                                    {isBookingExpired && (
                                                        <Badge variant="danger" size="sm" className="whitespace-nowrap">Exp</Badge>
                                                    )}
                                                    {isDuplicated && <span title="Duplicated Contract" role="img" aria-label="Duplicated contract" className="w-2 h-2 rounded-full bg-amber-500" />}
                                                </div>
                                            );
                                        } else if (headerKey === 'bookingNumber') {
                                            // Invygo / Leasing / Daily / Monthly: one shared rule (contracts.logic contractCategory)
                                            const badgeLabel = contractCategory(row);

                                            // The Booking column shows the category only (Daily / Monthly / Invygo / Leasing);
                                            // the booking number stays in the data, search and Excel export.
                                            content = badgeLabel
                                                ? <CellTag>{badgeLabel}</CellTag>
                                                : <span className="ds-cell-empty">—</span>;
                                        } else if (headerKey === 'customer' && !canOpenCalculator) {
                                            content = <CellText title={val}>{val}</CellText>;
                                        } else if (headerKey === 'customer') {
                                            content = (
                                                <button
                                                    type="button"
                                                    onClick={() => onCustomerClick(row)}
                                                    className="hover:underline"
                                                    title={val}
                                                >
                                                    <CellText>{val}</CellText>
                                                </button>
                                            );
                                        } else if (headerKey === 'phoneNumber' && row.type === 'open' && canWhatsApp) {
                                            content = (
                                                <ContractsRowActions row={row} switchBackOf={switchBackOf} onCopy={onCopy} />
                                            );
                                        } else if (headerKey === 'phoneNumber') {
                                            content = <CellPhone>{val}</CellPhone>;
                                        } else if (headerKey === 'ejarPlate' || headerKey === 'invygoPlate') {
                                            const invygoRow = isInvygoContract(row);
                                            // Invygo rows: the Invygo Plate is the fingerprint - no fallback to the Ejar plate.
                                            const actualVal = val || (headerKey === 'invygoPlate' && !invygoRow ? row.ejarPlate : '');
                                            const isExp = isCarExpired(actualVal);
                                            const expD = getExpiryDate(actualVal);
                                            const normPlate = normalizeStr(actualVal);
                                            const duplicates = duplicatedContractsInfo[normPlate];
                                            const otherContract = duplicates?.find(c => c.contractNo !== row.contractNo);
                                            // Which car is this, for an Invygo contract: the Invygo car (fingerprint) or a yelo replacement?
                                            let carKind = null;
                                            if (invygoRow && actualVal) {
                                                const fp = normalizeStr(row.invygoPlate);
                                                if (headerKey === 'invygoPlate') carKind = 'invygo';
                                                else if (fp && normPlate !== fp) carKind = 'yelo';
                                                else if (fp) carKind = 'invygo';
                                            }
                                            const yeloSince = carKind === 'yelo'
                                                ? ((row.carHistory || []).filter(h => h.kind === 'yelo' && normalizeStr(h.plate) === normPlate).map(h => (h.from || '').slice(0, 10)).sort().pop() || '')
                                                : '';
                                            const yeloSinceText = yeloSince ? `${yeloSince.slice(8, 10)}/${yeloSince.slice(5, 7)}` : '';

                                            content = (
                                                <div className="flex flex-col items-center">
                                                    <div className="flex items-center gap-1">
                                                        <CellPlate tone={isExp ? 'danger' : carKind === 'invygo' ? 'info' : carKind === 'yelo' ? 'warning' : undefined}>{actualVal}</CellPlate>
                                                        {isExp && filterMode !== 'expired' && <Icon glyph={ICONS.alert} tone="danger" size="sm" label="Expired" />}
                                                    </div>
                                                    {carKind === 'invygo' && <CellMuted tone="info">Invygo car</CellMuted>}
                                                    {carKind === 'yelo' && <CellMuted tone="warning">yelo car{yeloSinceText ? ` · since ${yeloSinceText}` : ''}</CellMuted>}
                                                    {invygoRow && headerKey === 'invygoPlate' && !actualVal && <CellMuted>no fingerprint yet</CellMuted>}
                                                    {isExp && expD && <span className={`ds-cell-muted ${toneClass('danger')}`} title={(expD.all || []).map(x => `${x.kind} expired ${x.raw}`).join(' · ')}>{expD.kind} exp {expD.date}</span>}
                                                    {isExp && !expD && filterMode !== 'expired' && <CellMuted tone="danger">Exp</CellMuted>}
                                                    {otherContract && (
                                                        <button
                                                            type="button"
                                                            className="mt-1"
                                                            onClick={(e) => { e.stopPropagation(); onCopy(otherContract.contractNo, `Contract ${otherContract.contractNo} copied!`); }}
                                                            title="Click to copy contract number"
                                                        >
                                                            <Badge variant="warning" size="sm">Rented to: {otherContract.customer}</Badge>
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        } else if (headerKey === 'invygoModel') {
                                            const actualModel = val || row.ejarModel;
                                            const switchTab = filterMode === 'switchback' || filterMode === 'mismatch';
                                            // ready: back in the log (days since its Date IN) or Ready on Ejar
                                            const readyText = switchBack && (switchBack.via === 'log'
                                                ? `Ready: ${switchBack.days === 0 ? 'today' : `${switchBack.days} day${switchBack.days === 1 ? '' : 's'} ago`}`
                                                : 'Ready on Ejar');
                                            content = (
                                                <div className="flex flex-col items-center gap-1">
                                                    <CellText>{actualModel}</CellText>
                                                    {switchBack && switchTab && <Badge variant="success" size="sm">{readyText}</Badge>}
                                                    {mismatch && switchTab && mustSwitchBack(row) && <CellMuted tone="info">Switch back required</CellMuted>}
                                                    {mismatch && switchTab && !mustSwitchBack(row) && onSkipSwitchBack && !isEditing && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); onSkipSwitchBack(row); }}
                                                            className="text-xs text-purple-700 hover:underline"
                                                            title="The customer keeps the car they have now: it becomes the contract's own car"
                                                        >
                                                            Skip switch back
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        } else if (headerKey === 'netBalance') {
                                            // A DAILY contract: Ejar's own balance (owner, 26 Sep 2026), '—' until the
                                            // engine has read it - never 'Paid' from a stored 0. Monthly: the live balance.
                                            const daily = dailyEjarBalance(row, ejarBalances);
                                            const balance = daily ? (daily.balance ?? 0) : parseNumber(val);
                                            const isPositive = balance > 0;
                                            const isZero = balance === 0;

                                            // the same rule as the No Rent tab (utils/liveBalance hasNoRent - CON-11)
                                            const missingMoRent = row.type === 'open' && hasNoRent(row) && !(daily && daily.balance !== null);
                                            const unknownDaily = !!daily && daily.balance === null && !missingMoRent;
                                            // Invygo / company: settled with the accountant - '—', never a balance or 'Paid'
                                            // (owner, 28 Sep / 1 Oct 2026)
                                            const noMoneyNote = noBalanceNote(row);
                                            const ejarNote = daily && daily.balance !== null
                                                ? `Ejar's balance${daily.source === 'booking' ? ' (booked total less paid)' : ''}${daily.at ? `, read ${String(daily.at).slice(0, 16).replace('T', ' ')}` : ''}`
                                                : '';

                                            let tooltipText = '';
                                            if (row.comments && Array.isArray(row.comments) && row.comments.length > 0) {
                                                const latest = row.comments[row.comments.length - 1];
                                                const dStr = new Date(latest.timestamp).toLocaleDateString();
                                                tooltipText = `${latest.user} (${dStr}):\n${latest.text}`;
                                            } else if (row.adminComment) {
                                                tooltipText = `Comment: ${row.adminComment}`;
                                            }

                                            content = (
                                                <div
                                                    className={`flex flex-col items-center justify-center gap-1 w-full h-full ${tooltipText ? 'cursor-help' : ''}`}
                                                    title={tooltipText || ejarNote || undefined}
                                                >
                                                    {noMoneyNote ? (
                                                        <span className="ds-cell-empty" title={noMoneyNote}>—</span>
                                                    ) : missingMoRent ? (
                                                        <Badge variant="danger" size="sm">No rent</Badge>
                                                    ) : unknownDaily ? (
                                                        <span className="ds-cell-empty" title="Daily contract: Ejar's balance is not read yet">—</span>
                                                    ) : (
                                                        isZero ? (
                                                            <Badge variant="success" size="sm">Paid</Badge>
                                                        ) : (
                                                            // Owed (red) / credit (green) stays colour-coded: it is the balance status, not decoration.
                                                            <span className="ds-cell-num" style={{ color: isPositive ? 'var(--danger)' : 'var(--success)' }}>
                                                                {balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                                            </span>
                                                        )
                                                    )}
                                                    {daily && daily.balance !== null && !missingMoRent && <CellMuted>Ejar</CellMuted>}
                                                </div>
                                            );
                                        } else if (headerKey === 'issue') {
                                            const issue = getIssueForRow(row, maintenanceData);
                                            content = issue ? <CellText tone="danger">{issue}</CellText> : <Icon glyph={ICONS.check} size="sm" tone="success" label="No issue" />;
                                        } else if (headerKey === 'contractType') {
                                            const contractTypeDisplay = { open: 'Open', closed_invygo: 'Closed (Invygo)', closed_other: 'Closed (Other)' };
                                            content = <CellTag>{contractTypeDisplay[row.type] || row.type}</CellTag>;
                                        } else if (typeof val === 'string' || typeof val === 'number') {
                                            // Any other column (models, branch, contact …): plain one-line text.
                                            content = <CellText>{String(val)}</CellText>;
                                        }

                                        return (
                                            <TableCell key={headerKey} className="relative">
                                                {isEditing && (!editableColumns || editableColumns.includes(headerKey)) ? (
                                                    <EditableCell
                                                        label={headerDisplayNames[headerKey] || headerKey}
                                                        initialValue={editedData[i] ? editedData[i][headerKey] || '' : row[headerKey] || ''}
                                                        onSave={(val) => {
                                                            const newEditedData = [...editedData];
                                                            if (!newEditedData[i]) newEditedData[i] = { ...row };
                                                            newEditedData[i][headerKey] = val;
                                                            newEditedData[i]._dirty = true;
                                                            // the cells the user really touched: only these are saved
                                                            newEditedData[i]._touched = [...new Set([...(newEditedData[i]._touched || []), headerKey])];
                                                            setEditedData(newEditedData);
                                                        }}
                                                    />
                                                ) : content}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                        {/* Bottom Spacer - REQUIRED for Virtualization (Invisible) */}
                        {bottomSpacer > 0 && <tr style={{ height: bottomSpacer, border: 'none' }} />}
                    </TableBody>
                </Table>
            </div>
            {copyMenu && (
                <CopyMenu 
                    row={copyMenu.row} 
                    coords={copyMenu.coords} 
                    onClose={() => setCopyMenu(null)} 
                    onCopy={onCopy} 
                    switchBackOf={switchBackOf}
                />
            )}
        </div>
    );
};

export default memo(ContractsTable);
